import { Controller } from "@hotwired/stimulus"
// html2canvas — used to snapshot the clicked element when a user submits an
// issue. It's a UMD build; a bare `import "html2canvas"` gives esbuild the
// module export but NEVER sets window.html2canvas (the UMD wrapper attaches to
// the module system, not the global, once bundled), so the screenshot silently
// failed. Import the value and call it directly instead.
import html2canvasLib from "html2canvas"
const html2canvas = (typeof html2canvasLib === "function")
  ? html2canvasLib
  : (html2canvasLib && html2canvasLib.default) || (typeof window !== "undefined" ? window.html2canvas : null)

// Inspect-style issue logger. Clicking the toolbar bug icon enters
// PICKER MODE — the user moves their mouse over the page and the
// element under the cursor lights up like in Chrome DevTools. On
// click, we walk up the DOM tree capturing every readable hint
// (Stimulus controllers, section data attrs, aria-label, button
// text, …) and auto-fill the issue dialog. The user only gets asked
// for what we couldn't infer — usually just a one-sentence
// "What went wrong?" plus an optional details field.
export default class extends Controller {
  static targets = ["dialog", "body"]

  static PROBLEMS = [
    { key: "broken",      label: "Broken — doesn't work",   icon: "🚫" },
    { key: "confusing",   label: "Confusing UI / UX",        icon: "🤔" },
    { key: "missing",     label: "Missing feature I want",   icon: "💡" },
    { key: "improvement", label: "Improvement idea",         icon: "✨" },
    { key: "slow",        label: "Too slow",                 icon: "🐌" },
    { key: "visual",      label: "Visual / layout bug",      icon: "🎨" },
    { key: "data",        label: "Data / save issue",        icon: "💾" }
  ]

  static SEVERITIES = [
    { key: "low",    label: "Low" },
    { key: "normal", label: "Normal" },
    { key: "high",   label: "High" }
  ]

  disconnect() {
    this._teardownPicker()
  }

  // Toolbar button click → enter picker mode (not open the dialog
  // immediately). Once the user clicks a target, the dialog opens
  // pre-filled with the captured context.
  open(event) {
    event?.preventDefault?.()
    this._startPicker()
  }

  close(event) {
    event?.preventDefault?.()
    if (this.hasDialogTarget) this.dialogTarget.close()
  }

  // -------- PICKER MODE --------

  _startPicker() {
    // Already picking — do NOT attach a second set of listeners. A
    // duplicate capture-phase click handler that later leaks would swallow
    // every click on the page (invisible full-page block until reload).
    if (this._picking) return
    // Build the overlays the first time.
    if (!this._pickerEls) {
      const banner = document.createElement("div")
      banner.className = "issue-log-picker-banner"
      banner.innerHTML = `
        <span class="issue-log-picker-banner-dot"></span>
        Click the part that's not working — or press <kbd>Esc</kbd> to cancel.`
      const outline = document.createElement("div")
      outline.className = "issue-log-picker-outline"
      const label = document.createElement("div")
      label.className = "issue-log-picker-label"
      document.body.appendChild(banner)
      document.body.appendChild(outline)
      document.body.appendChild(label)
      this._pickerEls = { banner, outline, label }
    }
    this._pickerEls.banner.style.display = "flex"
    this._pickerEls.outline.style.display = "block"
    this._pickerEls.label.style.display = "block"
    document.body.classList.add("issue-log-picking")

    // Bind the handlers ONCE and reuse the same references forever, so
    // addEventListener is idempotent (a duplicate add is a no-op) and
    // removeEventListener always matches the exact function — a leaked,
    // click-swallowing listener can never accumulate.
    if (!this._onPickerMove) {
      this._onPickerMove  = (e) => this._onPickerMove_inner(e)
      this._onPickerClick = (e) => this._onPickerClick_inner(e)
      this._onPickerKey   = (e) => { if (e.key === "Escape") this._teardownPicker() }
    }
    this._picking = true
    document.addEventListener("mousemove", this._onPickerMove, true)
    document.addEventListener("click",     this._onPickerClick, true)
    document.addEventListener("keydown",   this._onPickerKey,   true)
  }

  _teardownPicker() {
    this._picking = false
    // Keep the handler references STABLE (do not null them) so
    // removeEventListener always matches the function we added.
    if (this._onPickerMove)  document.removeEventListener("mousemove", this._onPickerMove, true)
    if (this._onPickerClick) document.removeEventListener("click",     this._onPickerClick, true)
    if (this._onPickerKey)   document.removeEventListener("keydown",   this._onPickerKey,   true)
    if (this._pickerEls) {
      this._pickerEls.banner.style.display  = "none"
      this._pickerEls.outline.style.display = "none"
      this._pickerEls.label.style.display   = "none"
    }
    document.body.classList.remove("issue-log-picking")
  }

  _onPickerMove_inner(e) {
    if (!this._picking) return
    const el = this._elementAt(e)
    if (!el || el === this._hovered) return
    this._hovered = el
    const r = el.getBoundingClientRect()
    const o = this._pickerEls.outline
    o.style.top    = `${r.top}px`
    o.style.left   = `${r.left}px`
    o.style.width  = `${r.width}px`
    o.style.height = `${r.height}px`
    const ctx = this._captureContext(el)
    const lbl = this._pickerEls.label
    lbl.innerHTML = `<strong>${this._esc(ctx.label)}</strong><span>${this._esc(ctx.subtitle)}</span>`
    // Position label above the outline (or below if too close to top).
    const lblY = r.top > 40 ? r.top - 34 : r.bottom + 6
    lbl.style.top  = `${lblY}px`
    lbl.style.left = `${Math.max(8, r.left)}px`
  }

  _onPickerClick_inner(e) {
    // Defensive: if we're not actively picking this is a stray/leaked
    // listener — remove it and let the click through. NEVER preventDefault
    // here, or a leaked listener would silently swallow every page click.
    if (!this._picking) { document.removeEventListener("click", this._onPickerClick, true); return }
    // Don't capture clicks on our own picker UI.
    if (e.target.closest(".issue-log-picker-banner")) return
    if (e.target.closest(".issue-log-picker-outline")) return
    if (e.target.closest(".issue-log-picker-label")) return
    e.preventDefault()
    e.stopPropagation()
    const el = this._elementAt(e)
    if (!el) { this._teardownPicker(); return }
    this._capturedContext = this._captureContext(el)
    // Save the actual DOM node so we can screenshot it later — the
    // context object only carries strings.
    this._capturedEl = el
    this._teardownPicker()
    // ONE viewport-level capture with the picked element outlined in
    // red on the rendered image. Replaces the previous two-shot
    // approach (area + fullpage) — fewer round trips, faster to
    // complete, and the highlight makes "what they clicked" unambiguous
    // without needing a separate close-up.
    this._screenshotDataUrl = ""
    this._screenshotError = ""
    this._captureScreenshot(el)
    this._openDialogWithContext()
  }

  // Single viewport capture with the picked element marked by a
  // bright red OVERLAY drawn at the element's bounding-rect
  // coordinates. The overlay is appended directly to the cloned
  // body (not the picked element), so it never breaks the picked
  // element's layout and works even when the element has its own
  // outline / box-shadow / transform / scroll container. Wrapped
  // in a 5-second Promise.race so the tile can never sit on
  // "Capturing…" forever.
  async _captureScreenshot(el) {
    // Watchdog: NEVER leave the tile stuck on "Capturing…". If neither a
    // screenshot nor an error has been set within 10s — a missing lib, a sync
    // throw before the try below, or an unhandled rejection — degrade to a
    // clear message so the user can still submit the report. Cleared in the
    // finally on the normal path.
    clearTimeout(this._captureWatchdog)
    this._captureWatchdog = setTimeout(() => {
      if (!this._screenshotDataUrl && !this._screenshotError) {
        this._screenshotError = "Screenshot couldn’t be captured — you can still submit your report."
        this._render()
      }
    }, 10000)
    if (typeof html2canvas !== "function") {
      clearTimeout(this._captureWatchdog)
      this._screenshotError = "Screenshot isn’t available here — you can still submit your report."
      this._render()
      return
    }
    // Snapshot the bounding rect NOW (in viewport coords) — we lose
    // it once html2canvas clones the doc into an offscreen iframe.
    const r = el.getBoundingClientRect()
    const pickedRect = {
      top:    r.top + window.scrollY,
      left:   r.left + window.scrollX,
      width:  r.width,
      height: r.height
    }
    const W = window.innerWidth  || document.documentElement.clientWidth
    const H = window.innerHeight || document.documentElement.clientHeight
    // Wrap CanvasGradient.addColorStop so html2canvas can't blow up
    // when it computes a non-finite stop position (the screenshot
    // browser was reporting). Restored after the capture either way.
    const _origAddColorStop = CanvasGradient.prototype.addColorStop
    CanvasGradient.prototype.addColorStop = function (offset, color) {
      const safe = Number.isFinite(offset) ? Math.min(1, Math.max(0, offset)) : 0
      return _origAddColorStop.call(this, safe, color)
    }
    // html2canvas can throw SYNCHRONOUSLY during setup on some pages; keep it
    // inside the try so that never escapes as an unhandled rejection (which
    // would strand the tile on "Capturing…"). The finally restores the patch.
    try {
    const capture = html2canvas(document.body, {
      backgroundColor: "#ffffff",
      // Render at full device pixel ratio so the captured screenshot
      // is crisp enough for an admin to actually READ at zoom. Was
      // 0.5 which produced near-illegible thumbnails. Modern devices
      // are 1.5–2x; cap at 2 so we don't generate huge canvases.
      scale: Math.min(2, window.devicePixelRatio || 1),
      logging: false,
      useCORS: true,
      imageTimeout: 1500,
      // Viewport-only: bounded box around what the user was actually
      // looking at when they reported the bug.
      x: window.scrollX,
      y: window.scrollY,
      width:  W,
      height: H,
      ignoreElements: (node) => node.classList?.contains("issue-log-picker-banner") ||
                                 node.classList?.contains("issue-log-picker-outline") ||
                                 node.classList?.contains("issue-log-picker-label") ||
                                 node.classList?.contains("issue-log-dialog"),
      onclone: (clonedDoc, _clonedRoot) => {
        // 0) Strip modern CSS color() functions (oklch/display-p3/etc.)
        //    that html2canvas can't parse. Walks every <style> block
        //    + inline style attr and replaces color() with currentColor
        //    so the render keeps the right intent without throwing.
        this._stripModernColorFunctionsInClone(clonedDoc)
        // 1) Privacy redaction (skipped for admins per Issue #9)
        if (!this._isAdminReporter()) this._redactCloneForPrivacy(clonedDoc.body)
        // 2) Drop a bright red overlay at the picked element's
        //    coordinates so the rendered viewport unmistakably
        //    shows where the user clicked.
        this._stampPickedOverlayOnClone(clonedDoc, pickedRect)
      }
    })
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Took too long — submit without screenshot")), 8000)
    )
      const canvas = await Promise.race([capture, timeout])
      // Downscale very large captures to keep the data URL under ~3 MB
      // while preserving enough resolution for the admin to actually
      // READ the captured UI. 2400px wide is sharp enough for typical
      // viewport thumbnails without bloating storage.
      const maxW = 2400
      let outCanvas = canvas
      if (canvas.width > maxW) {
        const ratio = maxW / canvas.width
        outCanvas = document.createElement("canvas")
        outCanvas.width = maxW
        outCanvas.height = Math.round(canvas.height * ratio)
        outCanvas.getContext("2d").drawImage(canvas, 0, 0, outCanvas.width, outCanvas.height)
      }
      // JPEG quality 0.9 (was 0.82) for a crisper render — the
      // post-resize data still stays comfortably under the storage
      // limit on typical viewports.
      this._screenshotDataUrl = outCanvas.toDataURL("image/jpeg", 0.9)
      this._screenshotError = ""
      this._render()
    } catch (err) {
      const msg = (err && err.message) || String(err)
      console.warn("[issue-log] screenshot capture failed/timed out", err)
      this._screenshotDataUrl = ""
      // Suppress noisy html2canvas known-limitation warnings (modern
      // CSS color() function, unsupported gradient interpolation, etc.)
      // — those aren't actionable errors and shouldn't bleed into the
      // user-facing preview. The issue still submits without a shot.
      if (/unsupported color function|Could not parse|color\(\s*display|color\(\s*srgb/i.test(msg)) {
        this._screenshotError = "Screenshot skipped — page uses modern CSS color() that html2canvas can't render."
      } else if (/addColorStop|non-finite|CanvasGradient/i.test(msg)) {
        // html2canvas computed a NaN/Infinity gradient stop position.
        // Same class of limitation as color() — degrade gracefully so
        // the issue still submits without the screenshot.
        this._screenshotError = "Screenshot skipped — page has a CSS gradient html2canvas can't render."
      } else if (/took too long/i.test(msg)) {
        this._screenshotError = msg
      } else {
        this._screenshotError = msg.slice(0, 200)
      }
      this._render()
    } finally {
      clearTimeout(this._captureWatchdog)
      // Always undo the addColorStop patch so we don't leak the
      // override into the rest of the page's runtime.
      try { CanvasGradient.prototype.addColorStop = _origAddColorStop } catch (_) {}
    }
  }

  // Inside the html2canvas clone (original DOM untouched), drop a
  // bright red overlay rectangle at the picked element's bounding-
  // rect coordinates. Anchoring the marker to absolute body coords
  // instead of the element itself means:
  //   - the picked element's layout / position / scroll container
  //     can't interfere with the marker
  //   - even if the element has its own outline / shadow / transform
  //     the marker still shows up on top
  //   - we don't need to find the element again via querySelector
  // html2canvas chokes on the modern CSS `color()` function (P3 /
  // oklch / srgb-linear) — it logs "Attempting to parse an unsupported
  // color function 'color'" and bails on the whole render. Walk the
  // cloned doc and neutralize those values before html2canvas runs.
  //
  // We replace `color(...)` references with the keyword `currentColor`
  // (or `transparent` for background-image gradients) so the rendered
  // intent is preserved even if the exact wide-gamut value is lost.
  _stripModernColorFunctionsInClone(clonedDoc) {
    if (!clonedDoc) return
    // Match color(...) AND oklch(...) AND oklab(...) — all wide-gamut
    // functions html2canvas's parser doesn't understand.
    const COLOR_FN = /\b(color|oklch|oklab|color-mix)\([^)]*\)/gi
    try {
      // 1) Inline style attrs on every element
      clonedDoc.querySelectorAll("[style]").forEach(el => {
        const s = el.getAttribute("style") || ""
        if (!COLOR_FN.test(s)) return
        el.setAttribute("style", s.replace(COLOR_FN, "currentColor"))
      })
      // 2) <style> tag contents (Tailwind v4 etc. emit color() heavily —
      //    including inside CSS custom properties on :root which my
      //    earlier scan via getPropertyValue missed because regex test
      //    state is stateful across calls when /g is set).
      clonedDoc.querySelectorAll("style").forEach(node => {
        const t = node.textContent || ""
        if (!/\b(color|oklch|oklab|color-mix)\(/i.test(t)) return
        node.textContent = t.replace(COLOR_FN, "currentColor")
      })
      // (Earlier we dropped <link rel="stylesheet"> tags entirely to
      //  avoid html2canvas hitting unparseable color() in external
      //  sheets — but that destroyed layout and produced a near-blank
      //  capture. Now we keep the links; step 3 below mutates the
      //  cssRules in place for same-origin sheets, which is enough
      //  for our app's Tailwind output.)
      // 3) External CSSStyleSheets: rewrite rules in-place. Skipped if
      //    cross-origin — the catch swallows that case so we don't
      //    throw and stop the pipeline.
      const sheets = clonedDoc.styleSheets
      for (let i = 0; i < sheets.length; i++) {
        try {
          const rules = sheets[i].cssRules
          if (!rules) continue
          for (let j = 0; j < rules.length; j++) {
            const r = rules[j]
            if (!r.style) continue
            for (let k = 0; k < r.style.length; k++) {
              const prop = r.style[k]
              const v = r.style.getPropertyValue(prop)
              if (v && COLOR_FN.test(v)) {
                r.style.setProperty(prop, v.replace(COLOR_FN, "currentColor"))
              }
            }
          }
        } catch (_) { /* cross-origin or otherwise unreadable — skip */ }
      }
    } catch (_) { /* defensive — never let the sanitizer break the render */ }
  }

  _stampPickedOverlayOnClone(clonedDoc, rect) {
    if (!rect || !rect.width || !rect.height) return
    try {
      // Pad the rect a few pixels so the outline doesn't crop the
      // element edge; cap negative coords at 0 so the marker stays
      // visible if the element is near the top/left.
      const pad = 4
      const top    = Math.max(0, rect.top    - pad)
      const left   = Math.max(0, rect.left   - pad)
      const width  = rect.width  + pad * 2
      const height = rect.height + pad * 2

      const marker = clonedDoc.createElement("div")
      marker.setAttribute("data-issue-log-marker", "1")
      marker.setAttribute("style", [
        "position:absolute",
        `top:${top}px`,
        `left:${left}px`,
        `width:${width}px`,
        `height:${height}px`,
        "border:5px solid #dc2626",
        "border-radius:6px",
        "background:rgba(220, 38, 38, 0.08)",
        "box-shadow:0 0 0 8px rgba(220, 38, 38, 0.25), 0 0 0 16px rgba(220, 38, 38, 0.10), 0 14px 40px -6px rgba(220, 38, 38, 0.55)",
        "pointer-events:none",
        "z-index:2147483647"   // top of the stacking context
      ].join(";"))
      clonedDoc.body.appendChild(marker)

      // Labeled badge anchored to the top-left of the marker.
      const badge = clonedDoc.createElement("div")
      badge.textContent = "🎯 PICKED"
      const badgeTop = Math.max(0, top - 28)
      badge.setAttribute("style", [
        "position:absolute",
        `top:${badgeTop}px`,
        `left:${left}px`,
        "background:#dc2626",
        "color:#ffffff",
        "font:800 12px/1 Inter, Arial, sans-serif",
        "letter-spacing:0.05em",
        "padding:5px 10px",
        "border-radius:5px",
        "pointer-events:none",
        "z-index:2147483647",
        "box-shadow:0 4px 14px -2px rgba(220,38,38,0.6)"
      ].join(";"))
      clonedDoc.body.appendChild(badge)
    } catch (_) { /* defensive — never let a marker error block the screenshot */ }
  }

  // Get the element under (x, y) honoring our own picker overlay (we
  // momentarily pointer-events:none it via CSS).
  _elementAt(e) {
    return document.elementFromPoint(e.clientX, e.clientY)
  }

  // Issue #9: admins skip the privacy redaction so screenshots they
  // file as debugging context show the real text. Reads the
  // `user-is-admin` meta tag (set by the Rails layout) once per
  // controller instance.
  _isAdminReporter() {
    if (this._adminCached != null) return this._adminCached
    const m = document.querySelector('meta[name="user-is-admin"]')
    this._adminCached = (m?.getAttribute("content") === "1")
    return this._adminCached
  }

  // Privacy pass run on the html2canvas clone right before render.
  // Replaces every visible text run with █ characters of the same
  // length so the layout is preserved but no real text leaks into
  // the saved screenshot. Form field values and selected options
  // get the same treatment.
  //
  // We DO NOT redact:
  //   - <script> / <style> contents (irrelevant — not rendered)
  //   - SVG <text> elements (kept so icon-only buttons remain
  //     distinguishable in the screenshot)
  //   - HTML attributes (aria-label, title) — those are diagnostic
  //     hints used by html2canvas, not visible on the canvas
  _redactCloneForPrivacy(root) {
    if (!root) return
    const mask = (s) => String(s == null ? "" : s).replace(/\S/g, "█")
    // 1. Text nodes anywhere in the subtree
    try {
      const walker = document.createTreeWalker(
        root,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            const p = node.parentNode
            if (!p) return NodeFilter.FILTER_REJECT
            const tag = (p.nodeName || "").toLowerCase()
            if (tag === "script" || tag === "style" || tag === "text") return NodeFilter.FILTER_REJECT
            return NodeFilter.FILTER_ACCEPT
          }
        }
      )
      const nodes = []
      while (walker.nextNode()) nodes.push(walker.currentNode)
      nodes.forEach(n => { n.nodeValue = mask(n.nodeValue) })
    } catch (_) { /* defensive — keep rendering even if walker fails */ }
    // 2. Form values + placeholders
    try {
      root.querySelectorAll("input, textarea").forEach(el => {
        const type = (el.type || "").toLowerCase()
        if (type === "checkbox" || type === "radio") return
        if (el.value) el.value = mask(el.value)
        const ph = el.getAttribute("placeholder")
        if (ph) el.setAttribute("placeholder", mask(ph))
        // Strip the actual <input> value attribute too (html2canvas
        // re-reads it when re-serializing on some browsers).
        if (el.getAttribute("value")) el.setAttribute("value", mask(el.getAttribute("value")))
      })
      // Selected option text + every option label (selects render the
      // current selection's visible text).
      root.querySelectorAll("select").forEach(sel => {
        Array.from(sel.options || []).forEach(o => {
          if (o.text) o.text = mask(o.text)
          if (o.value && /\s|\d|@|\./.test(o.value)) o.value = mask(o.value)
        })
      })
    } catch (_) { /* same — fall through */ }
    // 3. Background-image data URLs that might be screenshots of
    // documents (rare, but worth zeroing) — skip for now to keep
    // the layout intact.
  }

  // -------- CONTEXT CAPTURE --------

  // Walk up from the clicked element collecting hints we can read.
  // Returns { label, subtitle, area, function, selector, sectionId,
  // controller, action, pageUrl }.
  _captureContext(el) {
    const ctx = {
      label: "(unlabeled control)",
      subtitle: el.tagName.toLowerCase(),
      area: "",
      function: "",
      issueId: this._readIssueId(el),
      selector: this._buildSelector(el),
      sectionId: "",
      controller: "",
      action: "",
      ariaLabel: "",
      buttonText: "",
      pageUrl: window.location.href
    }
    // Closest semantic anchors
    const sectionEl  = el.closest("[data-section]")
    const controller = el.closest("[data-controller]")
    const actionEl   = el.closest("[data-action]")
    const buttonEl   = el.closest("button, a, [role=button]")
    const fieldEl    = el.closest("input, textarea, select, label")
    if (sectionEl)  ctx.sectionId  = sectionEl.dataset.section || ""
    if (controller) ctx.controller = (controller.dataset.controller || "").split(/\s+/)[0]
    if (actionEl)   ctx.action     = actionEl.dataset.action || ""
    if (buttonEl) {
      ctx.ariaLabel  = buttonEl.getAttribute("aria-label") || ""
      ctx.buttonText = (buttonEl.innerText || buttonEl.textContent || "").trim().slice(0, 80)
    } else if (fieldEl) {
      ctx.ariaLabel  = fieldEl.getAttribute("aria-label") || ""
    }
    // Multi-pass label resolution — fall through to whichever hint
    // first yields a non-empty, non-junk string. Order matters: the
    // explicit labels win, then the visible text, then derived hints.
    ctx.function = this._resolveFunctionLabel(el, buttonEl, fieldEl, actionEl, controller) || ""
    // Friendly area name from sectionId + controller
    ctx.area = this._areaLabel(ctx.sectionId, ctx.controller)
    // Hover label + subtitle for the picker overlay
    ctx.label = ctx.function || ctx.controller || ctx.sectionId || "Element"
    ctx.subtitle = [ctx.area, ctx.controller].filter(Boolean).join(" · ") || el.tagName.toLowerCase()
    return ctx
  }

  // Walk every hint we can think of, in priority order, until we find
  // a non-empty label. Even a generic-but-correct label (like the
  // Stimulus controller name) beats "(unlabeled control)".
  _resolveFunctionLabel(el, buttonEl, fieldEl, actionEl, controllerEl) {
    const clean = (s) => (s || "").toString().trim().replace(/\s+/g, " ").slice(0, 80)
    // 1. Explicit aria-label on the clicked element or its closest button
    const a = clean(el.getAttribute("aria-label") || buttonEl?.getAttribute("aria-label"))
    if (a) return a
    // 2. Title attribute (used heavily on toolbar tooltips)
    const t = clean(el.getAttribute("title") || buttonEl?.getAttribute("title"))
    if (t) return t
    // 3. Visible button / link text (trimmed, no nested whitespace)
    if (buttonEl) {
      const bt = clean(buttonEl.innerText || buttonEl.textContent)
      if (bt) return bt
    }
    // 4. Form field: label[for=…] OR wrapping <label> OR placeholder OR name
    if (fieldEl) {
      const labelFor = fieldEl.id && document.querySelector(`label[for="${CSS.escape(fieldEl.id)}"]`)
      const lt = clean(labelFor?.innerText)
      if (lt) return lt
      const wrap = fieldEl.closest("label")
      if (wrap && wrap !== fieldEl) {
        const wt = clean(wrap.innerText)
        if (wt) return wt
      }
      const ph = clean(fieldEl.getAttribute("placeholder"))
      if (ph) return ph
      const nm = clean(fieldEl.getAttribute("name") || fieldEl.id)
      if (nm) return nm
    }
    // 5. Useful data-* hints: data-chip / data-value / data-section-key / data-tab
    for (const attr of ["data-chip","data-value","data-section-key","data-tab","data-mode","data-age","data-surname","data-age-group"]) {
      const v = clean(el.getAttribute(attr) || buttonEl?.getAttribute(attr) || actionEl?.getAttribute(attr))
      if (v) return v
    }
    // 6. Closest heading inside the same section / dialog
    const head = el.closest("section, fieldset, dialog, [data-section]")?.querySelector("h1, h2, h3, .q-title, .q-person-title")
    const ht = clean(head?.innerText)
    if (ht) return ht
    // 7. Stimulus action — split "click->goal-resources#calculate" into the friendly tail
    const action = actionEl?.getAttribute("data-action") || el.getAttribute("data-action")
    if (action) {
      const m = action.match(/#(\w+)/)
      if (m) return this._humanize(m[1])
    }
    // 8. Stimulus controller name — last semantic fallback
    const ctrl = controllerEl?.getAttribute("data-controller")
    if (ctrl) {
      const first = ctrl.split(/\s+/)[0]
      if (first) return this._humanize(first) + " control"
    }
    // 9. Element tag + a hint about its role
    if (el.matches("input,textarea,select")) return el.tagName.toLowerCase() + " field"
    if (el.matches("svg, path, i, span"))    return "(graphic — pick the parent button next time)"
    return "(unlabeled control)"
  }

  // "loadSample" / "goal-resources" → "Load Sample" / "Goal Resources"
  _humanize(s) {
    return String(s || "")
      .replace(/[-_]+/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\s+/g, " ")
      .trim()
      .split(" ")
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ")
  }

  _areaLabel(sectionId, controller) {
    if (sectionId) {
      // Capitalize and pretty-format common section keys.
      const m = {
        client: "Client", about: "About", circleOfSupport: "Circle of Support",
        communication: "Communication", wonderfulThings: "Strengths",
        notSoFavorites: "Not Favorites", dayInLife: "Day in Life",
        important: "Important", bigDreams: "Big Dreams",
        supportMatch: "Support Match", working: "Working",
        barriers: "Barriers", goals: "Goals",
        currentServices: "Current Services", unmetNeeds: "Unmet Needs",
        resourceCalendar: "Service Calendar", review: "Review",
        facilitator: "Facilitator"
      }
      return m[sectionId] || sectionId
    }
    if (controller === "wizard")        return "Wizard"
    if (controller === "editor-native") return "Editor"
    if (controller === "print-preview") return "PDF Preview"
    if (controller === "issue-log")     return "Issue logger"
    return ""
  }

  // Read the auto-tagger's data-issue-id from the closest ancestor
  // that has one. This is the stable identifier admins can use to
  // jump to the exact element from a resolved issue.
  _readIssueId(el) {
    if (!el) return ""
    const owner = el.closest("[data-issue-id]")
    return owner ? owner.dataset.issueId : ""
  }

  // Best-effort short CSS selector — id wins, else tag.class up to 3
  // classes, else nth-child within a known section.
  _buildSelector(el) {
    if (!el) return ""
    if (el.id) return `#${el.id}`
    const tag = el.tagName.toLowerCase()
    const cls = Array.from(el.classList).slice(0, 3).join(".")
    return cls ? `${tag}.${cls}` : tag
  }

  // -------- DIALOG --------

  _openDialogWithContext() {
    if (!this.hasDialogTarget) return
    this._state = {
      step: "what",         // "what" | "details"
      problem: "",
      severity: "normal",
      details: "",
      attachConsole: false   // opt-in: ship last 60s of console buffer
    }
    this._render()
    this.dialogTarget.showModal()
  }

  pickProblem(event) {
    this._state.problem = event.currentTarget.dataset.value
    this._state.step = "details"
    this._render()
  }

  pickSeverity(event) {
    this._state.severity = event.currentTarget.dataset.value
    this._render()
  }

  updateDetails(event) {
    this._state.details = event.currentTarget.value
  }

  toggleConsoleAttach(event) {
    this._state.attachConsole = !!event.currentTarget.checked
  }

  // Build the `console_log` POST field — opt-in only. Filters the
  // ring buffer to the last 60 seconds, redacts PII unless the
  // reporter is an admin (mirrors the screenshot redaction posture),
  // and JSON-stringifies. Returns "" when the checkbox is off or
  // there's nothing recent.
  _buildConsoleLogField() {
    if (!this._state.attachConsole) return ""
    const all = Array.isArray(window.__pcpmConsoleLog) ? window.__pcpmConsoleLog : []
    const cutoff = Date.now() - 60_000
    const recent = all.filter(e => e && (e.t || 0) >= cutoff)
    if (!recent.length) return ""
    const safe = this._isAdminReporter()
      ? recent
      : recent.map(e => ({ ...e, text: this._redactConsoleText(e.text || "") }))
    try { return JSON.stringify(safe) } catch (_) { return "" }
  }

  // Light PII scrub for non-admin reporters. Matches the screenshot's
  // _redactCloneForPrivacy intent but operates on raw text strings
  // (console output, not DOM nodes). Whole-token swaps so the
  // surrounding sentence stays readable.
  _redactConsoleText(s) {
    if (!s) return ""
    return String(s)
      .replace(/\b[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}\b/gi, "[email]")
      .replace(/\b\d{3}[-.\s]?\d{3}[-.\s]?\d{4}\b/g, "[phone]")
      .replace(/(authorization:\s*bearer\s+|token=|api[_-]?key=)\S+/gi, "$1[redacted]")
      .replace(/\b\d{5}(-\d{4})?\b/g, "[zip]")
      .replace(/\b\d{6,}\b/g, "[id]")
  }

  back(event) {
    event?.preventDefault?.()
    if (this._state.step === "details") this._state.step = "what"
    this._render()
  }

  // Re-enter the picker if the user wants to pick a different element.
  repick(event) {
    event?.preventDefault?.()
    this.close()
    setTimeout(() => this._startPicker(), 50)
  }

  async submit(event) {
    event?.preventDefault?.()
    const problem = this.constructor.PROBLEMS.find(p => p.key === this._state.problem)
    if (!problem) {
      this._setError("Pick a problem type first.")
      return
    }
    const ctx = this._capturedContext || {}
    const titleArea = ctx.area || "PCP Maker"
    const titleFn   = ctx.function ? ` · ${ctx.function}` : ""
    const title = `[${titleArea}${titleFn}] ${problem.label}`
    const descParts = [
      `Area: ${ctx.area || "(unknown)"}`,
      `Function: ${ctx.function || "(unknown)"}`,
      `Problem: ${problem.label}`,
      `Page: ${ctx.pageUrl || window.location.href}`
    ]
    if (ctx.issueId)    descParts.push(`Issue-ID: ${ctx.issueId}`)
    if (ctx.sectionId)  descParts.push(`Section: ${ctx.sectionId}`)
    if (ctx.controller) descParts.push(`Controller: ${ctx.controller}`)
    if (ctx.action)     descParts.push(`Action: ${ctx.action}`)
    if (ctx.selector)   descParts.push(`Selector: ${ctx.selector}`)
    if (this._state.details.trim()) {
      descParts.push("", "User notes:", this._state.details.trim())
    }
    // Console buffer ships in its OWN field now (`console_log`), not
    // baked into the description. Opt-in only — empty string when the
    // reporter didn't tick the "Attach last 60s" checkbox.
    const consoleLog = this._buildConsoleLogField()
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ""
    try {
      const res = await fetch("/issue_logs", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrf
        },
        body: JSON.stringify({
          issue_log: {
            title:       title.slice(0, 200),
            description: descParts.join("\n"),
            severity:    this._state.severity,
            page_url:    ctx.pageUrl || window.location.href,
            console_log: consoleLog
          },
          screenshot_data_url: this._screenshotDataUrl || ""
        })
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.ok) {
        this._setError((json.errors || ["Couldn't save — try again."]).join(", "))
        return
      }
      this.close()
      window.location.href = `/issue_logs/${json.id}`
    } catch (e) {
      this._setError("Network error — try again.")
    }
  }

  _setError(msg) {
    const el = this.element.querySelector(".issue-log-error")
    if (el) el.textContent = msg
  }

  // -------- RENDER --------

  _render() {
    if (!this.hasBodyTarget) return
    const ctx = this._capturedContext || {}
    let body = ""
    switch (this._state.step) {
      case "what":    body = this._renderWhat(ctx);    break
      case "details": body = this._renderDetails(ctx); break
    }
    this.bodyTarget.innerHTML = body
  }

  _renderContextCard(ctx) {
    return `
      <div class="issue-log-context">
        <div class="issue-log-context-row">
          <span class="issue-log-context-label">You clicked</span>
          <strong class="issue-log-context-val">${this._esc(ctx.function || "(unlabeled control)")}</strong>
        </div>
        ${ctx.area ? `
          <div class="issue-log-context-row">
            <span class="issue-log-context-label">In</span>
            <span class="issue-log-context-val">${this._esc(ctx.area)}</span>
          </div>` : ""}
        ${ctx.issueId ? `
          <div class="issue-log-context-row">
            <span class="issue-log-context-label">ID</span>
            <code class="issue-log-context-code">${this._esc(ctx.issueId)}</code>
          </div>` : ""}
        ${ctx.action ? `
          <div class="issue-log-context-row">
            <span class="issue-log-context-label">Action</span>
            <code class="issue-log-context-code">${this._esc(ctx.action)}</code>
          </div>` : ""}
        <button type="button" class="issue-log-repick" data-action="issue-log#repick">
          ↻ Pick a different element
        </button>
      </div>`
  }

  _renderWhat(ctx) {
    return `
      ${this._renderContextCard(ctx)}
      ${this._renderScreenshotPreview()}
      <h3 class="issue-log-q">What's wrong with it?</h3>
      <p class="issue-log-sub">Pick the closest match — we'll skip the questions we already know.</p>
      <div class="issue-log-chip-grid">
        ${this.constructor.PROBLEMS.map(p => `
          <button type="button"
                  class="issue-log-chip"
                  data-action="issue-log#pickProblem"
                  data-value="${this._esc(p.key)}">
            <span class="issue-log-chip-icon">${p.icon}</span>
            <span class="issue-log-chip-label">${this._esc(p.label)}</span>
          </button>`).join("")}
      </div>`
  }

  // Single viewport screenshot preview with red-outlined picked
  // element. One tile, three states: capturing, ready, failed.
  _renderScreenshotPreview() {
    const privBadge = this._isAdminReporter()
      ? `<span class="issue-log-shot-priv" style="background:#fef3c7; color:#92400e; border-color:#fde68a;">🛠 Admin — text NOT redacted</span>`
      : `<span class="issue-log-shot-priv">🔒 Text auto-redacted for privacy</span>`
    let body
    if (this._screenshotDataUrl) {
      body = `<img class="issue-log-shot-img" src="${this._screenshotDataUrl}" alt="Screenshot with picked element highlighted">`
    } else if (this._screenshotError) {
      // Known html2canvas limitations (modern CSS color(), oklch,
      // gradients) get a quiet neutral hint instead of an alarming
      // red box. Real errors (timeouts, generic failures) still get
      // the louder treatment so we know to look at them.
      const isKnown = /Screenshot skipped/.test(this._screenshotError)
      const klass   = isKnown ? "issue-log-shot-skip" : "issue-log-shot-err"
      body = `<div class="${klass}">${this._esc(this._screenshotError.slice(0, 160))}</div>`
    } else {
      body = `<div class="issue-log-shot-pending-msg">Capturing & highlighting your selection…</div>`
    }
    return `<div class="issue-log-shot">
              <div class="issue-log-shot-label">📷 Screenshot will be attached
                <span class="issue-log-shot-priv" style="background:#fee2e2; color:#991b1b; border-color:#fecaca;">🔴 Picked element outlined</span>
                ${privBadge}
              </div>
              ${body}
            </div>`
  }

  _renderDetails(ctx) {
    const problem = this.constructor.PROBLEMS.find(p => p.key === this._state.problem)
    return `
      ${this._renderContextCard(ctx)}
      <div class="issue-log-mini-summary">
        <strong>Problem:</strong> ${this._esc(problem?.label || "")}
      </div>
      ${this._renderScreenshotPreview()}
      <h3 class="issue-log-q">Anything to add? <span class="issue-log-optional">(optional)</span></h3>
      <p class="issue-log-sub">Most of the context was captured from your click — only type if you want to add anything.</p>
      <textarea rows="3"
                placeholder="What were you expecting to happen? (optional)"
                data-action="input->issue-log#updateDetails">${this._esc(this._state.details)}</textarea>
      <div class="issue-log-sev-row">
        <span class="issue-log-sev-label">Urgency</span>
        ${this.constructor.SEVERITIES.map(s => `
          <button type="button"
                  class="issue-log-sev-chip ${this._state.severity === s.key ? "is-on" : ""}"
                  data-action="issue-log#pickSeverity"
                  data-value="${this._esc(s.key)}">${this._esc(s.label)}</button>`).join("")}
      </div>
      ${this._renderConsoleAttachRow()}
      <div class="issue-log-error"></div>
      <div class="issue-log-foot">
        <button type="button" class="issue-log-back" data-action="issue-log#back">← Back</button>
        <button type="button" class="issue-log-submit" data-action="issue-log#submit">Submit</button>
      </div>`
  }

  // Opt-in row that lets the reporter attach the last 60s of captured
  // console activity (errors / warnings / unhandled rejections). Shows
  // a live count; disables itself when the buffer has nothing recent.
  _renderConsoleAttachRow() {
    const buf = Array.isArray(window.__pcpmConsoleLog) ? window.__pcpmConsoleLog : []
    const cutoff = Date.now() - 60_000
    const count = buf.filter(e => e && (e.t || 0) >= cutoff).length
    const empty = count === 0
    const checked = !empty && this._state.attachConsole
    return `
      <label class="issue-log-attach-row ${empty ? "is-empty" : ""}">
        <input type="checkbox"
               ${checked ? "checked" : ""}
               ${empty ? "disabled" : ""}
               data-action="change->issue-log#toggleConsoleAttach">
        <span>📜 Attach last 60s of console
          <span class="issue-log-attach-count">(${empty ? "none" : count + (count === 1 ? " entry" : " entries")})</span>
        </span>
      </label>`
  }

  _esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
      .replace(/"/g,"&quot;").replace(/'/g,"&#39;")
  }
}
